import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef, RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../core/state/state.service';
import {ArchivedPortfolioDTO} from '../../../models/archived-portfolio-dto.model';
import {VehiclePortfolioLoadService} from '../../../services/vehicle-portfolio-load.service';

@Component({
    selector: 'app-vehicle-portfolio-archive',
    templateUrl: './vehicle-portfolio-archive.component.html',
    styleUrl: './vehicle-portfolio-archive.component.scss',
    standalone: false
})
export class VehiclePortfolioArchiveComponent implements OnInit {
  showSpinner: boolean;

  archiveList: ArchivedPortfolioDTO[];
  selectedRow: ArchivedPortfolioDTO;

  colDefinition: ColDef[] = [
    {field: 'archiveName', headerName: 'Archive Name', filter: AutocompleteFilterComponent},
    {field: 'createdBy', headerName: 'Created By', filter: AutocompleteFilterComponent},
    {field: 'creationDate', headerName: 'Creation Date'}
  ];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  constructor(public readonly vehPortfolioService: VehiclePortfolioLoadService,
              public readonly stateService: StateService,
              public readonly snackBar: FabMessageService,
              readonly dialog: MatDialog) {

  }

  ngOnInit(): void {
    this.getArchiveList();
  }

  /**
   * Archive the current business plan vehicle portfolio
   */
  onArchiveCurrentPortfolio(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Archive Current Portfolio',
        fieldLabel: 'Archive Name',
        buttonLabel: 'Confirm',
        maxLength: 50
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.showSpinner = true;
        this.vehPortfolioService.archiveCurrentPortfolio(res.data.newValue, this.stateService.getUserGmid()).subscribe({
          error: err => {
            this.showSpinner = false;
            this.getArchiveList();
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            const message = 'Sucessfully created archive ' + res.data.newValue;
            this.snackBar.displaySuccess(message, DISMISS_MSG);
            this.getArchiveList();
          }
        });
      }
    });
  }

  /**
   * Get all archived portfolios
   */
  getArchiveList(): void {
    this.showSpinner = true;
    this.vehPortfolioService.getArchivedPortfolios().subscribe((data: any) => {
      this.archiveList = data.map((row) => {
        return {archiveName: row.ARCHIVE_NAME, creationDate: row.CREATION_TSTAMP, createdBy: row.CREATED_BY}
      });
    }, err => {
      this.showSpinner = false;
      this.snackBar.displayError(err, DISMISS_MSG);
    }, () => {
      this.showSpinner = false;
    });
  }

  /**
   * Event handler to be able to edit an archive name
   */
  onUpdate(): void {
    const dialogRef = this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update',
        currentValueToUpdate: this.selectedRow.archiveName,
        hidePreview: true,
        maxLength: 50
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.showSpinner = true;
        this.vehPortfolioService.updateArchiveName(this.selectedRow.archiveName, res.data.newValue).subscribe({
          error: err => {
            this.showSpinner = false;
            this.getArchiveList();
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            const message = 'Successfully re-named ' + this.selectedRow.archiveName + ' to ' + res.data.newValue;
            this.snackBar.displaySuccess(message, DISMISS_MSG);
            this.getArchiveList();
            this.selectedRow = undefined;
          }
        });
      }
    });
  }

  /**
   * Event handler to delete a selected archive
   */
  onDelete(): void {
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: 'Delete',
        valueToDelete: this.selectedRow.archiveName
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res.data.deleteValue) {
        this.showSpinner = true;
        this.vehPortfolioService.deleteArchive(this.selectedRow.archiveName).subscribe({
          error: err => {
            this.showSpinner = false;
            this.getArchiveList();
            this.snackBar.displayError(err, DISMISS_MSG);
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully deleted archive ' + this.selectedRow.archiveName, DISMISS_MSG);
            this.getArchiveList();
            this.selectedRow = undefined;
          }
        });
      }
    });
  }

  /**
   * Event handler for selecting a row
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedRow = (event.data);
    } else if (event.data.archiveName === this.selectedRow.archiveName) {
      this.selectedRow = undefined;
    }
  }
}
